import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Lock, Star, UserPlus } from "lucide-react";
import type { DismissReason, MatchAction, MatchActionResponse, MatchCard, PartyView } from "@hub/shared";
import { DISMISS_REASONS, DISMISS_REASON_LABELS } from "@hub/shared";
import { Button, Field, Input, Pill, Textarea, toast } from "@/ui";
import { invokeEngine, useOpenContact } from "../_shims";
import { badgeTone, costsCredits, partyLabel, possessive, scoreText, scoreTone } from "../lib/cards";
import { CREDITS_VISIBLE } from "../lib/credits";
import { payloadToContactInput } from "../lib/contactPayload";
import { useMatchAction } from "../lib/hooks";
import { PartyBubble } from "./PartyAvatar";
import { PerspectiveHelp } from "./PerspectiveHelp";
import { InvitePanel } from "./InvitePanel";
import s from "../network.module.css";

function Party({ party, role }: { party: PartyView; role: string }) {
  const open = useOpenContact();
  const label = partyLabel(party);
  return (
    <div className={s.party}>
      <PartyBubble party={party} size={48} />
      <div className={s.partyName}>
        {party.hidden && (
          <span className={s.lock} aria-hidden>
            <Lock size={13} />
          </span>
        )}
        <span>{label}</span>
      </div>
      <div className={s.partyRole}>{role}</div>
      {party.hidden ? (
        <div className={`${s.small} ${s.muted}`}>Details appear after an accepted introduction.</div>
      ) : (
        <>
          {party.email && <div className={`${s.small} ${s.muted}`}>{party.email}</div>}
          {party.isHubUser && <Pill tone="info">On Hub</Pill>}
          {party.contactId && (
            <Button size="sm" variant="ghost" onClick={() => open(party.contactId!)}>
              Open person
            </Button>
          )}
        </>
      )}
    </div>
  );
}

const BAR_ROWS: { key: "text" | "location" | "network" | "activity"; label: string; max: number }[] = [
  { key: "text", label: "Text match", max: 1 },
  { key: "location", label: "Location", max: 0.2 },
  { key: "network", label: "Network", max: 0.1 },
  { key: "activity", label: "Activity", max: 0.1 },
];

export function ScoreBars({ card, breakdown }: { card: MatchCard; breakdown?: Partial<Record<"text" | "location" | "network" | "activity", number>> | null }) {
  // The card carries only the total; a breakdown is shown when the server attaches one.
  const rows = breakdown ? BAR_ROWS.filter((r) => typeof breakdown[r.key] === "number") : [];
  return (
    <div className={s.block}>
      <div className={s.blockLabel}>Why this match</div>
      <div className="u-row" style={{ marginBottom: rows.length ? 8 : 0 }}>
        <Pill tone={scoreTone(card.scoreLabel)}>{scoreText(card.scoreLabel, card.score)}</Pill>
        <span className={`${s.small} ${s.muted}`}>{card.matchType === "direct" ? "Both people are in your address book." : card.matchType === "network" ? "Reached through one of your connections." : "Reached across Hub networks."}</span>
      </div>
      {rows.length > 0 && (
        <div className={s.bars} aria-label="Score breakdown">
          {rows.map((r) => {
            const v = breakdown![r.key] ?? 0;
            return (
              <div key={r.key} style={{ display: "contents" }}>
                <span>{r.label}</span>
                <span className={s.barTrack}>
                  <span className={s.barFill} style={{ width: `${Math.min(100, (v / r.max) * 100)}%` }} />
                </span>
                <span className={s.barValue}>{Math.round(v * 100)}%</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Stars({ value, onChange }: { value: number | null; onChange: (v: 1 | 2 | 3 | 4 | 5) => void }) {
  return (
    <div className={s.stars} role="radiogroup" aria-label="Rating">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" role="radio" aria-checked={value === n} aria-label={`${n} star${n === 1 ? "" : "s"}`} className={s.star} data-on={value !== null && n <= value ? "true" : "false"} onClick={() => onChange(n as 1 | 2 | 3 | 4 | 5)}>
          <Star size={16} fill={value !== null && n <= value ? "currentColor" : "none"} />
        </button>
      ))}
    </div>
  );
}

export interface MatchDetailProps {
  card: MatchCard;
  /** Preselects an action (e.g. the row's primary button for actions that need a note). */
  initialAction?: MatchAction | null;
  onClose?: () => void;
}

/**
 * The whole introduction: both parties, the need and the service, score, the server's action list, note and
 * confirmation, the revealed panel after accept, and a rating step that posts mark_complete.
 */
export function MatchDetail({ card, initialAction = null, onClose }: MatchDetailProps) {
  const navigate = useNavigate();
  const action = useMatchAction(card.matchId);
  const [pending, setPending] = useState<MatchAction | null>(initialAction);
  const [note, setNote] = useState("");
  const [rating, setRating] = useState<1 | 2 | 3 | 4 | 5 | null>(null);
  const [reason, setReason] = useState<DismissReason | null>(null);
  const [result, setResult] = useState<MatchActionResponse | null>(null);
  const [addedContactId, setAddedContactId] = useState<string | null>(null);
  const [ratingDone, setRatingDone] = useState(false);

  useEffect(() => setPending(initialAction), [initialAction, card.matchId]);
  // The confirm panel opens under the action list, often below the dialog's fold: bring it into view.
  const pendingRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (pending) pendingRef.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [pending]);

  const current = result?.card ?? card;
  const needsNote = pending?.requiresNote ?? false;
  const noteMissing = needsNote && note.trim().length === 0;

  const run = async (a: MatchAction, extra: { note?: string | null; rating?: 1 | 2 | 3 | 4 | 5 | null; reason?: DismissReason | null } = {}) => {
    try {
      const res = await action.mutateAsync({ action: a.id, note: extra.note ?? null, rating: extra.rating ?? null, ...(a.id === "dismiss" ? { reason: extra.reason ?? null } : {}) });
      setResult(res);
      setPending(null);
      setNote("");
      setReason(null);
      if (CREDITS_VISIBLE && res.creditsSpent > 0) toast(`${res.creditsSpent} credit${res.creditsSpent === 1 ? "" : "s"} used.`);
      if (a.id === "mark_complete") setRatingDone(true);
      if (res.conversationId && (a.id === "message" || a.id === "request_introduction")) {
        toast("Message sent.", "success", { label: "Open conversation", onClick: () => navigate(`/messages/${res.conversationId}`) });
      } else if (res.invites && res.invites.length > 0) {
        toast("Invitations are ready below. Send each person their link.", "success");
      } else if (a.id !== "accept_match") {
        toast(`${a.label}: done.`, "success");
      }
      if (a.id === "dismiss" || a.id === "decline") onClose?.();
    } catch (e) {
      toast(e instanceof Error ? e.message : "The action failed.", "danger");
    }
  };

  const choose = (a: MatchAction) => {
    if (a.disabled) return;
    if (a.requiresNote || costsCredits(a) || a.id === "mark_complete" || a.id === "dismiss") {
      setPending(a);
      return;
    }
    void run(a);
  };

  const addToPeople = async () => {
    if (!result?.revealed) return;
    try {
      const input = payloadToContactInput(result.revealed.contactPayload, { name: result.revealed.name, email: result.revealed.email, phone: result.revealed.phone });
      const created = await invokeEngine("createContact", input);
      setAddedContactId(created.contactId);
      toast(created.duplicates.length > 0 ? "Added. A similar person already exists; check for duplicates." : "Added to your people.", "success");
    } catch (e) {
      toast(e instanceof Error ? e.message : "Could not add the person.", "danger");
    }
  };

  // The revealed person may already be in your people (the card links them): open them rather than add a copy.
  const knownContactId = result?.revealed ? ([current.helper, current.needer].find((p) => p.contactId && !p.hidden && ((result.revealed!.email && p.email === result.revealed!.email) || p.name === result.revealed!.name))?.contactId ?? null) : null;

  const completeAction = current.actions.find((a) => a.id === "mark_complete") ?? { id: "mark_complete", label: "Mark complete", description: null, icon: "check", creditCost: 0, creditEarned: 0, creditLabel: "Free", isPrimary: false, requiresNote: false, recipientUserId: null, disabled: false, disabledReason: null } satisfies MatchAction;

  return (
    <div className={s.detail} data-testid="match-detail">
      <div className="u-row" style={{ flexWrap: "wrap" }}>
        <Pill tone={badgeTone(current.display.badgeColor)} title={current.scenarioName}>
          {current.display.badge}
        </Pill>
        <Pill tone="neutral">{current.status.replace(/_/g, " ")}</Pill>
        <span className={`${s.small} ${s.muted}`}>{current.scenarioName}</span>
        <span className="u-grow" />
        <PerspectiveHelp card={current} />
      </div>

      <div className={s.parties}>
        <Party party={current.needer} role={`Needs: ${current.need.title}`} />
        <ArrowRight className={s.arrow} size={20} aria-hidden />
        <Party party={current.helper} role={`Can help: ${current.service.title}`} />
      </div>

      <div className={s.block}>
        <div className={s.blockLabel}>The need</div>
        <div style={{ fontWeight: 600 }}>{current.need.title}</div>
        {current.need.description && <div className={`${s.small} ${s.muted}`}>{current.need.description}</div>}
        <div className={s.chips}>
          <span className={s.chip}>{current.need.category}</span>
          {current.need.timeline && <span className={s.chip}>{current.need.timeline}</span>}
        </div>
      </div>
      <div className={s.block}>
        <div className={s.blockLabel}>The offer</div>
        <div style={{ fontWeight: 600 }}>{current.service.title}</div>
        {current.service.description && <div className={`${s.small} ${s.muted}`}>{current.service.description}</div>}
        <div className={s.chips}>
          <span className={s.chip}>{current.service.category}</span>
        </div>
      </div>

      <ScoreBars card={current} breakdown={(current as MatchCard & { scoreBreakdown?: Record<string, number> }).scoreBreakdown ?? null} />

      <InvitePanel card={current} invites={current.invites ?? []} />

      {result?.revealed && (
        <div className={s.revealed} data-testid="revealed-panel">
          <div style={{ fontWeight: 600 }}>Introduction accepted</div>
          <div>
            <div style={{ fontWeight: 600 }}>{result.revealed.name}</div>
            {result.revealed.email && <div className={s.small}>{result.revealed.email}</div>}
            {result.revealed.phone && <div className={s.small}>{result.revealed.phone}</div>}
          </div>
          <div className="u-row">
            {(addedContactId ?? knownContactId) ? (
              <Button size="sm" variant="outline" onClick={() => navigate(`/contact/${addedContactId ?? knownContactId}`)}>
                Open person
              </Button>
            ) : (
              <Button size="sm" variant="primary" icon={<UserPlus size={14} />} onClick={addToPeople}>
                Add to my people
              </Button>
            )}
            {result.conversationId && (
              <Button size="sm" variant="ghost" onClick={() => navigate(`/messages/${result.conversationId}`)}>
                Open conversation
              </Button>
            )}
          </div>
          {!ratingDone ? (
            <div className="u-col" style={{ gap: 6 }}>
              <div className={s.small}>How did it go? Rate the introduction to close it.</div>
              <div className="u-row">
                <Stars value={rating} onChange={setRating} />
                <Button size="sm" disabled={rating === null || action.isPending} loading={action.isPending} onClick={() => run(completeAction, { rating })}>
                  Mark complete
                </Button>
              </div>
            </div>
          ) : (
            <div className={s.small}>Thanks. This introduction is complete.</div>
          )}
        </div>
      )}

      <div className={s.block}>
        <div className={s.blockLabel}>Actions</div>
        {current.actions.length === 0 ? (
          <div className={`${s.small} ${s.muted}`}>Nothing to do right now.</div>
        ) : (
          <div className={s.actionList}>
            {current.actions.map((a) => (
              <button key={a.id} type="button" className={s.actionItem} data-primary={a.isPrimary ? "true" : undefined} disabled={a.disabled || action.isPending} title={a.disabledReason ?? a.description ?? undefined} onClick={() => choose(a)} data-testid={`action-${a.id}`}>
                <span style={{ minWidth: 0 }}>
                  <span className={s.actionLabel}>{a.label}</span>
                  {a.description && <div className={s.actionDesc}>{a.description}</div>}
                  {a.disabled && a.disabledReason && <div className={s.actionDesc}>{a.disabledReason}</div>}
                </span>
                {CREDITS_VISIBLE && <Pill tone={a.creditCost > 0 ? "warning" : a.creditEarned > 0 ? "success" : "neutral"}>{a.creditLabel}</Pill>}
              </button>
            ))}
          </div>
        )}
      </div>

      {pending && (
        <div ref={pendingRef} className={s.pendingPanel} data-testid="pending-panel">
          <div style={{ fontWeight: 600 }}>{pending.label}</div>
          {pending.description && <div className={s.small}>{pending.description}</div>}
          {costsCredits(pending) && (
            <div className={s.small} data-testid="action-confirm">
              {CREDITS_VISIBLE ? `This uses ${pending.creditCost} credit${pending.creditCost === 1 ? "" : "s"}. ` : ""}Hub never contacts anyone for you; the other side decides whether to accept.
            </div>
          )}
          {CREDITS_VISIBLE && pending.creditEarned > 0 && <div className={s.small}>You earn {pending.creditEarned} credit{pending.creditEarned === 1 ? "" : "s"} if it is accepted.</div>}
          {pending.requiresNote && (
            <Field label="Your note" hint="Required. Say what you need or why you are reaching out. Only the person you write to sees it." error={noteMissing && note.length > 0 ? null : undefined}>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Hi, I saw you can help with..." autoFocus aria-label="Note" />
            </Field>
          )}
          {pending.id === "dismiss" && (
            <div className="u-col" style={{ gap: 6 }} data-testid="dismiss-reasons">
              <div className={s.small}>Why? Optional; it helps Hub find better introductions. The other side never sees it.</div>
              <div className={s.chips} role="radiogroup" aria-label="Why dismiss">
                {DISMISS_REASONS.map((r) => (
                  <button key={r} type="button" role="radio" aria-checked={reason === r} className={s.chip} data-selectable="true" data-on={reason === r ? "true" : undefined} onClick={() => setReason(reason === r ? null : r)}>
                    {DISMISS_REASON_LABELS[r]}
                  </button>
                ))}
              </div>
              {reason === "other" && <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Say more (optional)" aria-label="Say more" maxLength={500} autoFocus />}
            </div>
          )}
          {pending.id === "mark_complete" && (
            <div className="u-col" style={{ gap: 6 }}>
              <div className={s.small}>Optional: rate how it went.</div>
              <Stars value={rating} onChange={setRating} />
            </div>
          )}
          <div className="u-row">
            <Button variant="primary" size="sm" disabled={noteMissing || action.isPending} loading={action.isPending} onClick={() => run(pending, { note: pending.id === "dismiss" && reason !== "other" ? null : note.trim() || null, rating, reason })} data-testid="confirm-action">
              {CREDITS_VISIBLE && costsCredits(pending) ? `Confirm, use ${pending.creditCost} credit${pending.creditCost === 1 ? "" : "s"}` : pending.requiresNote ? "Send" : pending.id === "dismiss" ? "Dismiss" : "Confirm"}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { setPending(null); setReason(null); }}>
              Cancel
            </Button>
            {noteMissing && (
              <span className={`${s.small} ${s.muted}`} data-testid="note-required">
                A note is required for this action.
              </span>
            )}
          </div>
        </div>
      )}

      {current.needer.hidden || current.helper.hidden ? (
        <div className={`${s.small} ${s.muted}`}>
          {current.needer.hidden ? `${possessive(current.needer.ownerName)} contact` : `${possessive(current.helper.ownerName)} contact`} stays hidden until their owner shares them or the introduction is accepted.
        </div>
      ) : null}
    </div>
  );
}
